import {
  Box,
  Button,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material"
import { useMemo, useState } from "react"

import { ProgramStatus } from "@features/students-tab/studentsTab.slice"
import appIcons from "@utils/appIcons"
import { domainToDisplayName } from "@utils/domainUtils"
import { IconType } from "@utils/iconType"
import { StudentAcademicStatusDTO } from "./studentAcademicStatus.dto"
import {
  AttentionSignalKey,
  CSAT_FLAG_MAX,
  NO_SESSION_AFTER_DAYS,
  NPS_FLAG_MAX,
  PROGRESS_STALL_AFTER_DAYS,
} from "./studentFlags"
import { ColumnKeys } from "./students-grid/columns/column.keys"
import { controlButtonSx } from "./students-grid/filters/students.table.toolbar"

// What a pick means: either a grid filter item (column predicates execute
// through the grid's filter model, exactly where they did before) or one of
// the page-level params the client-side filters already run on. The page owns
// applying it — this component only names the predicate.
export type PickedPredicate =
  | { kind: "grid"; field: string; operator: string; value: string }
  | { kind: "needsAttention" }
  | { kind: "signal"; signal: AttentionSignalKey }
  | { kind: "mine" }

// The four attention signals as pickable predicates. Labels derive from the
// same thresholds studentFlags flags with, so the chip can never promise a
// different cut than the data applies — and they say it in words, not
// notation ("stalled >14d" sent Mayer asking what stalled meant).
// optionLabel leads a menu row (sentence case), chipLabel follows
// "Signal: " on a chip.
export const SIGNAL_PREDICATES: {
  key: AttentionSignalKey
  optionLabel: string
  chipLabel: string
}[] = [
  {
    key: "csat",
    optionLabel: `Low satisfaction (CSAT ${CSAT_FLAG_MAX} or less)`,
    chipLabel: `Low satisfaction`,
  },
  {
    key: "nps",
    optionLabel: `Low NPS (${NPS_FLAG_MAX} or less)`,
    chipLabel: `Low NPS`,
  },
  {
    key: "stall",
    optionLabel: `No progress in ${PROGRESS_STALL_AFTER_DAYS}+ days`,
    chipLabel: `No progress ${PROGRESS_STALL_AFTER_DAYS}+ days`,
  },
  {
    key: "noSession",
    optionLabel: `No live session in ${NO_SESSION_AFTER_DAYS}+ days`,
    chipLabel: `No session ${NO_SESSION_AFTER_DAYS}+ days`,
  },
]

type FieldKey =
  | "school"
  | "class"
  | "mentor"
  | "signal"
  | "outcome"
  | "language"
  | "cohort"

const FILTER_FIELDS: { key: FieldKey; label: string }[] = [
  { key: "school", label: "School" },
  { key: "class", label: "Class" },
  { key: "mentor", label: "Mentor" },
  { key: "signal", label: "Signal" },
  { key: "outcome", label: "Outcome" },
  { key: "language", label: "Language" },
  { key: "cohort", label: "Cohort" },
]

// Fields that only mean something on one status stay visible but disabled,
// with the reason as a caption — hiding them would make the menu look broken
// on the other views.
function fieldGate(
  field: FieldKey,
  programStatus: ProgramStatus,
): string | null {
  if (field === "signal" && programStatus !== "active") {
    return "Only on active students"
  }
  if (field === "outcome" && programStatus !== "ended") {
    return "Only on completed students"
  }
  return null
}

type ValueOption = { label: string; predicate: PickedPredicate }

function distinctSorted(values: (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => Boolean(value))),
  ).sort((a, b) => a.localeCompare(b))
}

const PickerIcon = (props: { icon: IconType }) => (
  <SvgIcon
    component={props.icon}
    inheritViewBox
    sx={{ width: 16, height: 16, fill: "none", stroke: "currentColor" }}
  />
)

// The one filter editor: [+ Filter] opens a two-step popover — pick a field,
// then pick a value from what the loaded rows actually contain. It replaces
// the MUI grid filter panel (a form of operators nobody on this page needed);
// every pick lands as a chip in the strip and writes into the same state the
// views save and restore.
export const FilterFieldPicker = (props: {
  programStatus: ProgramStatus
  rows: StudentAcademicStatusDTO[]
  hasMentorRole: boolean
  onPick: (predicate: PickedPredicate) => void
}) => {
  const { programStatus, rows, hasMentorRole, onPick } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [field, setField] = useState<FieldKey | null>(null)
  const [query, setQuery] = useState("")

  const close = () => {
    setAnchorEl(null)
    setField(null)
    setQuery("")
  }

  const pick = (predicate: PickedPredicate) => {
    onPick(predicate)
    close()
  }

  // Value options come from the loaded rows — the picker offers only values
  // that can actually match something on the current view's data.
  const valueOptions = useMemo<ValueOption[]>(() => {
    switch (field) {
      case null:
        return []
      case "school":
        return distinctSorted(
          rows.map((row) => {
            const domain = row.registrationDetails?.domain
            return domain ? domainToDisplayName(domain) : null
          }),
        ).map((value) => ({
          label: value,
          predicate: {
            kind: "grid",
            field: ColumnKeys.PROGRAM,
            operator: "is",
            value,
          },
        }))
      case "class":
        // Dominant AND folded additional classes: the Class column's custom
        // "is" operator checks both, so every offered value really matches.
        return distinctSorted([
          ...rows.map((row) => row.registrationDetails?.className),
          ...rows.flatMap((row) => row.additionalClassNames ?? []),
        ]).map((value) => ({
          label: value,
          predicate: {
            kind: "grid",
            field: ColumnKeys.REGISTERED_CLASS,
            operator: "is",
            value,
          },
        }))
      case "mentor":
        return [
          // "Me" is the mentor's one-click predicate — it maps to the mine
          // scope (client-side, identity-based), not to a name match.
          ...(hasMentorRole
            ? [{ label: "Me", predicate: { kind: "mine" } as PickedPredicate }]
            : []),
          ...distinctSorted(rows.map((row) => row.staff?.mentor?.name)).map(
            (value) => ({
              label: value,
              predicate: {
                kind: "grid",
                field: ColumnKeys.MENTOR,
                operator: "equals",
                value,
              } as PickedPredicate,
            }),
          ),
        ]
      case "signal":
        return [
          {
            label: "Needs attention (any signal)",
            predicate: { kind: "needsAttention" },
          },
          ...SIGNAL_PREDICATES.map((signal) => ({
            label: signal.optionLabel,
            predicate: {
              kind: "signal",
              signal: signal.key,
            } as PickedPredicate,
          })),
        ]
      case "outcome":
        // Mirrors the Outcome column's valueGetter: end reasons capitalized,
        // a missing reason displays (and therefore filters) as "Ended".
        return distinctSorted(
          rows.map((row) => {
            const endReason = row.registrationDetails?.endReason
            return endReason
              ? endReason.charAt(0).toUpperCase() + endReason.slice(1)
              : "Ended"
          }),
        ).map((value) => ({
          label: value,
          predicate: {
            kind: "grid",
            field: ColumnKeys.OUTCOME,
            operator: "equals",
            value,
          },
        }))
      case "language":
        return distinctSorted(
          rows.map((row) => row.registrationDetails?.language),
        ).map((value) => ({
          label: value,
          predicate: {
            kind: "grid",
            field: ColumnKeys.LANGUAGE,
            operator: "equals",
            value,
          },
        }))
      case "cohort": {
        // Month-year labels exactly as the Cohort column formats them (the
        // filter compares against that column's valueGetter output), newest
        // cohort first.
        const newestByLabel = new Map<string, number>()
        for (const row of rows) {
          const cohort = row.registrationDetails?.cohort
          if (!cohort) continue
          const date = new Date(cohort)
          if (isNaN(date.getTime())) continue
          const label = date.toLocaleString("en-US", {
            month: "long",
            year: "2-digit",
          })
          const newest = newestByLabel.get(label)
          if (newest === undefined || date.getTime() > newest) {
            newestByLabel.set(label, date.getTime())
          }
        }
        return Array.from(newestByLabel.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([label]) => ({
            label,
            predicate: {
              kind: "grid",
              field: ColumnKeys.COHORT,
              operator: "is",
              value: label,
            },
          }))
      }
    }
  }, [field, rows, hasMentorRole])

  const visibleOptions =
    query.trim() === ""
      ? valueOptions
      : valueOptions.filter((option) =>
          option.label.toLowerCase().includes(query.trim().toLowerCase()),
        )

  const fieldLabel = FILTER_FIELDS.find((entry) => entry.key === field)?.label

  return (
    <>
      <Button
        size="small"
        data-testid="add-filter-control"
        startIcon={<PickerIcon icon={appIcons.plus} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={controlButtonSx}
      >
        Filter
      </Button>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: "280px" } } }}
      >
        {field === null ? (
          <MenuList>
            {FILTER_FIELDS.map((entry) => {
              const gate = fieldGate(entry.key, programStatus)
              return (
                <MenuItem
                  key={entry.key}
                  disabled={gate !== null}
                  onClick={() => setField(entry.key)}
                >
                  <ListItemText
                    primary={entry.label}
                    secondary={gate ?? undefined}
                    secondaryTypographyProps={{ variant: "body3" }}
                  />
                </MenuItem>
              )
            })}
          </MenuList>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", p: "8px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                pb: "8px",
              }}
            >
              <IconButton
                size="small"
                aria-label="Back to fields"
                onClick={() => {
                  setField(null)
                  setQuery("")
                }}
              >
                <PickerIcon icon={appIcons.arrowLeft} />
              </IconButton>
              <Typography variant="body2_sb">{fieldLabel}</Typography>
            </Box>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search values"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && visibleOptions.length > 0) {
                  event.preventDefault()
                  pick(visibleOptions[0].predicate)
                }
              }}
            />
            <MenuList sx={{ maxHeight: "280px", overflowY: "auto" }}>
              {visibleOptions.map((option, index) => (
                <MenuItem
                  key={`${index}-${option.label}`}
                  onClick={() => pick(option.predicate)}
                >
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
              {visibleOptions.length === 0 && (
                <Box sx={{ px: "8px", py: "8px" }}>
                  {/* body3, not caption: the theme's caption is uppercase by
                      design and this is sentence-case copy. */}
                  <Typography variant="body3" color="text.secondary">
                    No matching values in the loaded rows
                  </Typography>
                </Box>
              )}
            </MenuList>
          </Box>
        )}
      </Popover>
    </>
  )
}
